import React, { useMemo, useState } from 'react';
import SearchBar from '@frontend/Components/Inputs/SearchBar/SearchBar';
import StatusBadge from '@frontend/Components/StatusBadge/StatusBadge';
import Table from '@frontend/Components/Table/Table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import './CustomFieldsTablesPanel.css';

export interface CustomFieldRow {
  id: number;
  name: string;
  description?: string;
}

export interface CustomTableRow {
  id: number;
  name: string;
  description?: string;
}

export interface CustomFieldsTablesPanelProps {
  title?: string;
  searchPlaceholder?: string;
  customTables: CustomTableRow[];
  customFields: CustomFieldRow[];
  selectedTableIds: number[];
  selectedFieldIds: number[];
  onToggleTable: (id: number) => void;
  onToggleField: (id: number) => void;
  onAddGlobalTable?: () => void;
  className?: string;
}

type RowKind = 'Table' | 'Field';

type PanelRow = {
  kind: RowKind;
  id: number;
  name: string;
  description?: string;
  checked: boolean;
};

/**
 * CustomFieldsTablesPanel
 * Searchable list of custom tables + fields, with checkboxes and right-side tags.
 * Uses shared `SearchBar`, `StatusBadge`, and `Table`.
 */
const CustomFieldsTablesPanel: React.FC<CustomFieldsTablesPanelProps> = ({
  title = 'Custom Fields and Tables',
  searchPlaceholder = 'Search custom tables...',
  customTables,
  customFields,
  selectedTableIds,
  selectedFieldIds,
  onToggleTable,
  onToggleField,
  onAddGlobalTable,
  className = '',
}) => {
  const [term, setTerm] = useState('');

  const rows: PanelRow[] = useMemo(() => {
    const merged: PanelRow[] = [
      ...customTables.map((t) => ({
        kind: 'Table' as const,
        id: t.id,
        name: t.name,
        description: t.description,
        checked: selectedTableIds.includes(t.id),
      })),
      ...customFields.map((f) => ({
        kind: 'Field' as const,
        id: f.id,
        name: f.name,
        description: f.description,
        checked: selectedFieldIds.includes(f.id),
      })),
    ];

    if (!term.trim()) return merged;
    const q = term.toLowerCase();
    return merged.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        (r.description ?? '').toLowerCase().includes(q)
    );
  }, [customFields, customTables, selectedFieldIds, selectedTableIds, term]);

  const columns = useMemo(
    () => [
      {
        header: '',
        render: (row: PanelRow) => (
          <input
            type="checkbox"
            checked={row.checked}
            onChange={() => {
              if (row.kind === 'Table') onToggleTable(row.id);
              else onToggleField(row.id);
            }}
            aria-label={`Select ${row.kind.toLowerCase()} ${row.name}`}
          />
        ),
      },
      {
        header: '',
        render: (row: PanelRow) => (
          <div className="cftp-row">
            <div className="cftp-row__title">{row.name}</div>
            {row.description && (
              <div className="cftp-row__desc">{row.description}</div>
            )}
          </div>
        ),
      },
      {
        header: '',
        render: (row: PanelRow) => (
          <div className="cftp-row__tag">
            <StatusBadge
              status={row.kind}
              variant={row.kind === 'Table' ? 'in-review' : 'pending'}
              size="small"
            />
          </div>
        ),
      },
    ],
    [onToggleField, onToggleTable]
  );

  return (
    <div className={`cftp ${className}`}>
      <div className="cftp__title">{title}</div>

      <div className="cftp__panel">
        <div className="cftp__panel-top">
          <div className="cftp__search">
            <SearchBar
              onSearch={(value) => setTerm(value)}
              placeholder={searchPlaceholder}
              className="cftp__searchbar"
            />
          </div>

          {onAddGlobalTable && (
            <button
              type="button"
              className="cftp__add-global"
              onClick={onAddGlobalTable}
              aria-label="Add new global table"
            >
              <span className="cftp__add-global-icon" aria-hidden="true">
                <FontAwesomeIcon icon={faPlus} />
              </span>
              <span className="cftp__add-global-text">Add New Global Table</span>
            </button>
          )}
        </div>

        <div className="cftp__table">
          <Table
            columns={columns as any}
            data={rows}
            emptyMessage="No matching items"
            rowKey="id"
          />
        </div>
      </div>
    </div>
  );
};

export default CustomFieldsTablesPanel;


